/**
 * The closed vocabularies the practitioner's pathway screens show (contract §2): path
 * types, statuses and step kinds, each as "admin" i18n keys, plus the icon of each step
 * kind. One table per vocabulary, so every member has a key and no raw identifier reaches
 * the screen.
 *
 * The kind icons are the learner side's own (public/pathways), so a step reads the same
 * in the builder and on the learner's path.
 *
 * Pure apart from the icon components: the caller passes its bound `t` for "admin".
 */
import { CalendarClock, FileText, GraduationCap, HelpCircle, PenLine, Play } from "lucide-react";

const TYPE_KEYS = Object.freeze({
  successive: "path.type.successive",
  alternative: "path.type.alternative",
  level: "path.type.level",
});

const TYPE_HINT_KEYS = Object.freeze({
  successive: "path.type.successiveHint",
  alternative: "path.type.alternativeHint",
  level: "path.type.levelHint",
});

// "published" is labelled Active everywhere a practitioner reads it (spec screen 1).
const STATUS_KEYS = Object.freeze({
  draft: "path.status.draft",
  published: "path.status.published",
  archived: "path.status.archived",
});

const KIND_KEYS = Object.freeze({
  video: "path.kind.video",
  reading: "path.kind.reading",
  practice: "path.kind.practice",
  quiz: "path.kind.quiz",
  live: "path.kind.live",
  course: "path.kind.course",
});

const KIND_ICONS = Object.freeze({
  video: Play,
  reading: FileText,
  practice: PenLine,
  quiz: HelpCircle,
  live: CalendarClock,
  course: GraduationCap,
});

export const typeLabel = (t, type) => t(TYPE_KEYS[type] || TYPE_KEYS.successive);
export const typeHint = (t, type) => t(TYPE_HINT_KEYS[type] || TYPE_HINT_KEYS.successive);
export const statusLabel = (t, status) => t(STATUS_KEYS[status] || STATUS_KEYS.draft);
export const kindLabel = (t, kind) => t(KIND_KEYS[kind] || KIND_KEYS.reading);
/** @returns {React.ComponentType} the lucide icon of a step kind */
export const kindIcon = (kind) => KIND_ICONS[kind] || FileText;
/** "Level 2 · Practice"; "Level 2" while the level has no title yet. */
export const levelLabel = (t, n, title) => (title ? t("path.level.label", { n, title }) : t("path.level.labelUntitled", { n }));
